import { FilterTypes, ReportMenuLabels } from 'Reports/constants/reportsConstants';
import { ItemTypeConsts } from 'client/util/Constants';
import ReportsApi from 'client/api/v2/ReportsApi';

const fetchReport = (payload, cancelToken, params) => {
  if (params.route) {
    return ReportsApi.generateReport(
      { ...payload, cancelToken },
      params.route,
    );
  }

  return ReportsApi.fetchUsersToResourcesReport(
    { ...payload, cancelToken },
    params,
  );
};

/**
 * The values of this object are used to reference individual reports.
 */
export const ReportTypes = {
  browserPatchMgmtPolicy: 'browserPatchMgmtPolicy',
  commandResults: 'commandResults',
  osPatchMgmtPolicy: 'osPatchMgmtPolicy',
  userToDevice: 'userToDevice',
  userToDirectory: 'userToDirectory',
  userToLdap: 'userToLdap',
  userToRadius: 'userToRadius',
  userToSso: 'userToSso',
  userToUserGroup: 'userToUserGroup',
};

/**
 * This object lists information on each report.
 * Each object key is a ReportType and can be referenced from the ReportTypes object
 * @property {string} name - The name of the report displayed to the customer
 * @property {function} fetchReport - The method containing the API call used to generate the report
 * @property {array} filters - Array of filter types to narrow report output
 * @property {function} isEnabled - Function to dynamically check individual report's feature flag
 * @property {string} menuLabel - The menu label on the report type selector dropdown
 * @property {object} previewImage - The SVG image that is displayed on the Reports page
 * @property {array} resourcesNeeded - Each resource needed in order to generate a non-empty report
 * @property {string} type - Self-referential string used to dynamically retrieve menuLabel
 */
export default {
  browserPatchMgmtPolicy: {
    name: 'Browser Patch Management Policy',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { route: 'browser-patch-policy' }),
    filters: [],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.browserPatchReport),
    menuLabel: ReportMenuLabels.deviceReports,
    previewImage: '/adminportal/reports/reports-preview-browser-patch-policy.svg',
    resourcesNeeded: [ItemTypeConsts.browserPatchManagement, ItemTypeConsts.device],
    type: ReportTypes.browserPatchMgmtPolicy,
  },
  commandResults: {
    name: 'Command Results',
    fetchReport: () => Promise.resolve([]), // To be completed in FE-655
    filters: [FilterTypes.commands, FilterTypes.device, FilterTypes.deviceGroups],
    isEnabled: () => false,
    menuLabel: ReportMenuLabels.deviceReports,
    previewImage: undefined,
    resourcesNeeded: [ItemTypeConsts.device, ItemTypeConsts.commandResult],
  },
  osPatchMgmtPolicy: {
    name: 'OS Patch Management Policy',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { route: 'os-patch-policy' }),
    filters: [],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.osPatchMgmtPolicyReport),
    menuLabel: ReportMenuLabels.deviceReports,
    previewImage: '/adminportal/reports/reports-preview-os-patch-policy.svg',
    resourcesNeeded: [ItemTypeConsts.osPatchManagement, ItemTypeConsts.device],
    type: ReportTypes.osPatchMgmtPolicy,
  },
  userToDevice: {
    name: 'Users to Devices',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { resource: 'DEVICES' }),
    filters: [
      FilterTypes.deviceGroups,
      FilterTypes.devices,
      FilterTypes.userGroups,
      FilterTypes.users,
    ],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.usersToDevicesReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-devices.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.device],
    type: ReportTypes.userToDevice,
  },
  userToDirectory: {
    name: 'Users to Directories',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { resource: 'DIRECTORIES' }),
    filters: [FilterTypes.users],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.usersToDirectoriesReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-directories.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.directory],
    type: ReportTypes.userToDirectory,
  },
  userToLdap: {
    name: 'Users to LDAP Servers',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { resource: 'LDAP_SERVERS' }),
    filters: [FilterTypes.users],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.usersToLdapServersReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-ldap.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.ldap],
    type: ReportTypes.userToLdap,
  },
  userToRadius: {
    name: 'Users to RADIUS Servers',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { resource: 'RADIUS_SERVERS' }),
    filters: [FilterTypes.users],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.usersToRadiusServersReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-radius.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.radiusServer],
    type: ReportTypes.userToRadius,
  },
  userToSso: {
    name: 'Users to SSO Applications',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { route: 'users-to-sso' }),
    filters: [FilterTypes.users],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.ssoReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-applications.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.ssoApplications],
    type: ReportTypes.userToSso,
  },
  userToUserGroup: {
    name: 'Users to User Groups',
    fetchReport: (payload, { cancelToken } = {}) => fetchReport(payload, cancelToken, { route: 'users-to-user-groups' }),
    filters: [FilterTypes.users, FilterTypes.userGroups],
    isEnabled: (that) => that.$hasFeatureFlag(that.$featureFlags.usersToUserGroupsReport),
    menuLabel: ReportMenuLabels.userToResourceReports,
    previewImage: '/adminportal/reports/reports-preview-users-to-resources-user-groups.svg',
    resourcesNeeded: [ItemTypeConsts.systemUser, ItemTypeConsts.userGroup],
    type: ReportTypes.userToUserGroup,
  },
};
